import React, { Component } from 'react'
import { observer } from 'mobx-react'
import i18n from 'i18next'
import { cloneDeep } from 'lodash'
import * as R from 'ramda'
import Select from '../../form/Select'
import Modal from '../../../UniformComponents/Modal'
import Button from '../../../UniformComponents/Button'
import Panel from '../../../UniformComponents/Panel'
import { unescapeHtmlTags } from '../../../utils/helpers'
import InfoAccordion from '../../InfoAccordion'
import { trackBuilderQuestionMenuAction } from '../../../utils/MixpanelManager'

import './styles/questionMappingPopup.scss'

const questionOptionsPath = {
    TB: ['optionSetX', 'options'],
    NF: ['optionSet'],
}

// these questions don't need drag and drop as there is only 1 response to inherit to and from
const directMapQuestions = ['TA', 'TX', 'DT']

@observer
class QuestionPipe extends Component {
    showModal = false

    constructor(props) {
        super(props)
        this.state = {
            questionOptions: [],
            mappings: {},
            sourceOptions: [],
            usedOptions: {},
            rowsWarning: false,
            hasLiveResponses: false,
            sourceQuestionType: undefined,
            isGuidanceAccordionOpen: false,
        }
    }

    componentDidUpdate() {
        const { store } = this.props
        const { showQuestionPipe } = store
        console.log('componentDidUpdate', showQuestionPipe, this.showModal)
        if (showQuestionPipe !== this.showModal) {
            this.showModal = showQuestionPipe
            this.initState()
        }
    }

    initState = async () => {
        const { store } = this.props
        const { survey, questionPipeSettings } = store
        let sourceQuestion = null
        let sourceOptions = []
        let mappings = {}
        let questionType
        const usedOptions = {}
        const { targetQuestion } = questionPipeSettings

        const responsesReq = await store.http.get(`/questions/hasresponses?pageQuestionId=${targetQuestion.id}`, store.surveyToken)

        if (responsesReq.hasLiveResponses) {
            this.setState({ hasLiveResponses: true })
            return
        }

        let sourcePageQuestionId = null
        if (questionPipeSettings.mappings && !questionPipeSettings.mappings.gridMapping) {
            sourceQuestion = questionPipeSettings.options.find(({ value }) => {
                if (questionPipeSettings.mappings.set1) {
                    return value === `${questionPipeSettings.mappings.pageQuestionId}_${questionPipeSettings.mappings.set1}`
                }
                return value === questionPipeSettings.mappings.pageQuestionId
            })
            if (sourceQuestion) {
                const sourcePageQuestion = questionPipeSettings.pageQuestions.find(({ id }) => {
                    if (questionPipeSettings.mappings.set1) {
                        return id === `${questionPipeSettings.mappings.pageQuestionId}_${questionPipeSettings.mappings.set1}`
                    }
                    return id === questionPipeSettings.mappings.pageQuestionId
                })

                questionType = sourcePageQuestion.question.questionTypeCode
                const optionsPath = questionOptionsPath[questionType] || ['optionSet', 'options']

                sourceOptions = R.pathOr([], optionsPath, sourcePageQuestion.question).map((o) => ({
                    value: R.pathOr(o.id, ['question', 'id'], o),
                    label: unescapeHtmlTags(o.getTitle(
                        store.surveyLocale,
                        survey.defaultLocale,
                        sourcePageQuestion.question.metadata.initialLocale,
                    )),
                }))
            }
            mappings = Object.keys(questionPipeSettings.mappings.mapping).reduce((map, key) => {
                map[key] = questionPipeSettings.mappings.mapping[key].map((v) => {
                    const option = sourceOptions.find((so) => so.value === v)
                    return {
                        label: option ? unescapeHtmlTags(option.label) : 'invalid option',
                        value: v,
                    }
                })
                questionPipeSettings.mappings.mapping[key].forEach((v) => {
                    usedOptions[v] = true
                })
                return map
            }, {})
            sourcePageQuestionId = questionPipeSettings.mappings.pageQuestionId
        }

        if (questionPipeSettings.mappings && questionPipeSettings.mappings.gridMapping) {
            const { gridMapping } = questionPipeSettings.mappings
            sourceQuestion = questionPipeSettings.options.find(({ value }) => (
                value === questionPipeSettings.mappings.pageQuestionId
            ))
            const sourcePageQuestion = questionPipeSettings.pageQuestions.find(({ id }) => (
                id === questionPipeSettings.mappings.pageQuestionId
            ))

            questionType = sourcePageQuestion.question.questionTypeCode
            const optionsPath = questionOptionsPath[questionType] || ['optionSet', 'options']

            sourceOptions = R.pathOr([], optionsPath, sourcePageQuestion.question).map((o) => ({
                value: o.id,
                label: unescapeHtmlTags(o.getTitle(
                    store.surveyLocale,
                    survey.defaultLocale,
                    sourcePageQuestion.question.metadata.initialLocale,
                )),
            }))

            const allOptions = [
                ...R.pathOr([], ['question', 'optionSetY', 'options'], sourcePageQuestion),
                ...R.pathOr([], ['question', 'optionSetX', 'options'], sourcePageQuestion),
            ]

            const optionLabels = allOptions.reduce((obj, o) => {
                const newObj = { ...obj }
                newObj[o.id] = unescapeHtmlTags(o.getTitle(
                    store.surveyLocale,
                    survey.defaultLocale,
                    sourcePageQuestion.question.metadata.initialLocale,
                ))
                return newObj
            }, {})

            const rowMappings = Object.keys(gridMapping).reduce((rows, k) => {
                const newRows = { ...rows }
                newRows[k] = Object.keys(gridMapping[k]).map((key) => {
                    usedOptions[key] = true
                    return { value: key, type: 'row', label: optionLabels[key] }
                })
                return newRows
            }, {})

            const columns = Object.values(gridMapping[Object.keys(gridMapping)[0]])[0]

            const columnMappings = Object.keys(columns).reduce((cols, k) => {
                const newCols = { ...cols }
                newCols[k] = columns[k].map((key) => {
                    usedOptions[key] = true
                    return { value: key, label: optionLabels[key] }
                })
                return newCols
            }, {})
            mappings = { ...rowMappings, ...columnMappings }

            sourcePageQuestionId = questionPipeSettings.mappings.pageQuestionId
        }

        this.setState({
            questionOptions: [],
            sourceOptions,
            mappings,
            sourcePageQuestionId,
            sourceQuestion,
            usedOptions,
            hasLiveResponses: false,
            sourceQuestionType: questionType,
        })
    }

    hide = () => {
        const { store } = this.props
        store.questionPipeSettings = {
            options: [],
        }
        this.showModal = false
        store.showQuestionPipe = false
    }

    onChangeHandler = (obj) => {
        const { store } = this.props
        const { questionPipeSettings, survey } = store
        const { pageQuestions } = questionPipeSettings
        const { value } = obj
        const [questionId, rowId] = value.split('_')
        const sourcePageQuestion = pageQuestions.find((pq) => pq.id === value)
        const questionType = sourcePageQuestion.question.questionTypeCode
        const optionsPath = questionOptionsPath[questionType] || ['optionSet', 'options']
        const sourceOptions = R.pathOr([], optionsPath, sourcePageQuestion.question).map((o) => ({
            value: R.pathOr(o.id, ['question', 'id'], o),
            label: unescapeHtmlTags(o.getTitle(
                store.surveyLocale,
                survey.defaultLocale,
                sourcePageQuestion.question.metadata.initialLocale,
            )),
            rowId,
        }))

        this.setState({
            sourceQuestion: obj,
            sourcePageQuestionId: value,
            sourceOptions,
            mappings: {},
            usedOptions: {},
            sourceQuestionType: questionType,
        })
    }

    startDrag = (e) => {
        e.target.classList.add('dragSource')
        e.dataTransfer.setData('Text', e.target.dataset.index)
        console.log(`startDrag ${e.target.dataset.index}`)
        this.setState({
            dragging: true,
            rowsWarning: false,
        })
    }

    endDrag = (e) => {
        e.target.classList.remove('dragSource')
        this.setState({
            dragging: false,
        })
    }

    dragOver = (e) => {
        e.preventDefault()
    }

    sourceOption = (label, value, index) => {
        const { usedOptions } = this.state
        return (
            <div
                style={{
                    cursor: usedOptions[value] ? 'default' : 'grabbing',
                    opacity: usedOptions[value] ? 0.4 : 1,
                }}
                className="source-question__draggable-container"
                draggable={!usedOptions[value]}
                data-index={index}
                title={label}
            >
                <div className="source-question__draggable-handle">
                    <i className="fa-solid fa-grip-dots-vertical" />
                </div>
                <span>{label}</span>
            </div>
        )
    }

    drop = (e) => {
        const { sourcePageQuestionId, mappings: prevMappings, usedOptions: prevUsedOptions } = this.state
        let { sourceOptions } = this.state
        const { store } = this.props
        const { questionPipeSettings, survey } = store
        const { pageQuestions, targetQuestion } = questionPipeSettings
        const sourcePageQuestion = pageQuestions.find((pq) => pq.id === sourcePageQuestionId)
        const targetQuestionType = targetQuestion.question.questionTypeCode
        const oneToOneTypes = ['UV', 'OD', 'TL', 'TB']

        e.preventDefault()
        if (e.target.className.includes('dropTarget')) {
            let destinationOptions
            const [strSourceIndex, type] = e.dataTransfer.getData('Text').split('_')
            const isTargetGrid = targetQuestionType === 'TB'
            const isSourceTypeCol = e.target.className.includes('tb-col')
            const { question } = questionPipeSettings.targetQuestion
            const optionsPath = questionOptionsPath[targetQuestionType] || ['optionSet', 'options']

            if (isTargetGrid) {
                // if TB, cancel row->col and col->row drops, and set right destination
                if (isSourceTypeCol) {
                    if (type === 'row') return
                    destinationOptions = question.optionSetX.options
                } else {
                    if (type === 'col') return
                    destinationOptions = question.optionSetY.options
                }
            } else {
                destinationOptions = R.pathOr(undefined, optionsPath, question)
            }
            if (destinationOptions === undefined) return

            e.target.classList.remove('active')

            const sourceIndex = parseInt(strSourceIndex, 10)
            const targetIndex = parseInt(e.target.dataset.index, 10)
            console.log(`Drop ${sourceIndex} -> ${targetIndex}`)
            const mappings = cloneDeep(prevMappings)
            const usedOptions = cloneDeep(prevUsedOptions)

            const targetId = R.pathOr(
                destinationOptions[targetIndex].id, [targetIndex, 'question', 'id'], destinationOptions,
            )
            if (targetId === undefined) return
            if (oneToOneTypes.includes(targetQuestionType)
                && !isSourceTypeCol
                && R.pathOr([], [targetId], mappings).length) return

            // pick right sourceOptions
            let sourceId
            if (e.target.className.includes('tb-')) {
                const axis = isSourceTypeCol ? 'optionSetX' : 'optionSetY'
                sourceOptions = R.pathOr([], ['question', axis, 'options'], sourcePageQuestion)
                    .map((o) => ({
                        value: o.id,
                        label: unescapeHtmlTags(o.getTitle(
                            store.surveyLocale,
                            survey.defaultLocale,
                            sourcePageQuestion.question.metadata.initialLocale,
                        )),
                    }))
                sourceId = R.pathOr([], ['question', axis, 'options'], sourcePageQuestion)[sourceIndex].id
            } else {
                sourceId = sourceOptions[sourceIndex].value
            }

            if (oneToOneTypes.includes(targetQuestionType)
                && !isSourceTypeCol
                && R.pathOr([], [targetId], mappings).length) return

            if (!Object.hasOwnProperty.call(mappings, targetId)) {
                mappings[targetId] = []
            }

            if (mappings[targetId].every(({ value }) => value !== sourceId)) {
                mappings[targetId].push({
                    value: sourceId,
                    label: sourceOptions[sourceIndex].label,
                    type,
                })
            }
            usedOptions[sourceId] = true
            this.setState({
                mappings,
                usedOptions,
            })
        }
    }

    dragEnter = (e) => {
        if (e.target.className.includes('dropTarget')) {
            e.target.classList.add('active')
        }
    }

    dragLeave = (e) => {
        console.log(`dragLeave -> ${e.target.className}`)
        if (e.target.className.includes('dropTarget')) {
            console.log('dragLeave')
            e.target.classList.remove('active')
        }
    }

    removeMapping = (e) => {
        const { mappings: prevMappings, usedOptions: prevUsedOptions } = this.state
        console.log('removeMapping')
        const { oid, index } = e.target.dataset
        const mappings = cloneDeep(prevMappings)
        const usedOptions = cloneDeep(prevUsedOptions)
        Reflect.deleteProperty(usedOptions, mappings[oid][index].value)
        mappings[oid].splice(parseInt(index, 10), 1)
        console.log(mappings[oid], index)
        this.setState({
            mappings,
            usedOptions,
        })
    }

    targetMappings = (mappings, oId) => {
        let elem = null

        if (mappings) {
            elem = mappings.map(({ value, label }, index) => (
                <div className="source-question__draggable-container dropped" title={label}>
                    <div className="source-question__draggable-handle">
                        <i className="fa-solid fa-grip-dots-vertical" />
                    </div>
                    <span>{label}</span>
                    <i
                        className="fal fa-times-circle"
                        onClick={this.removeMapping}
                        data-index={index}
                        data-oid={oId}
                        style={{ cursor: 'pointer' }}
                    />
                </div>
            ))
        }

        return elem
    }

    targetOption = (o, index, className = 'dropTarget') => {
        const { store } = this.props
        const { mappings, dragging } = this.state
        const { survey, questionPipeSettings } = store
        const optionId = R.pathOr(o.id, ['question', 'id'], o)
        const droppedSourceResponses = this.targetMappings(mappings[optionId], optionId)
        const title = unescapeHtmlTags(o.getTitle(
            store.surveyLocale,
            survey.defaultLocale,
            questionPipeSettings.targetQuestion.question.metadata.initialLocale,
        ))
        return (
            <div
                className={`destination-question__item ${className}`}
                data-index={index}
            >
                <div className="destination-title" title={title}>
                    {title}
                </div>
                <div className="droppable-area" style={{ pointerEvents: (dragging ? 'none' : 'initial') }}>
                    <div className="mappings">
                        {droppedSourceResponses}
                    </div>
                    <p className="droppable-placeholder">{i18n.t('surveyBuilder.modal.mapping.drag_here')}</p>
                </div>
            </div>
        )
    }

    discard = () => {
        const { store } = this.props
        const { questionPipeSettings } = store
        const { targetQuestion } = questionPipeSettings

        trackBuilderQuestionMenuAction(store, { action: 'map responses', value: false })

        Reflect.deleteProperty(targetQuestion, 'populateFrom')
        targetQuestion.hasPopulateFrom = false

        const options = Object.assign(
            {},
            targetQuestion.currentOptions,
        )

        targetQuestion.update(options)

        this.hide()
    }

    save = async () => {
        const { store } = this.props
        const { sourcePageQuestionId, mappings, sourceQuestionType } = this.state
        const { questionPipeSettings } = store
        const { targetQuestion } = questionPipeSettings
        const questionType = targetQuestion.question.questionTypeCode
        const noMappingsQuestion = ['TX', 'TA', 'DT'].includes(questionType)
            || (sourceQuestionType === 'I' && questionType === 'I')
        let hasActiveMappings = false

        if (noMappingsQuestion) {
            hasActiveMappings = !!sourcePageQuestionId
        } else {
            hasActiveMappings = Object.keys(mappings).reduce((active, key) => (
                active || mappings[key].length
            ), false)
        }

        if (!hasActiveMappings) {
            this.discard()
            return
        }
        const [sourceQuestionId, sourceRowId] = sourcePageQuestionId.split('_')
        const TBColumns = {}
        const TBRows = {}

        if (questionType === 'TB') {
            if (!Object.values(mappings).flat(1).filter((m) => m.type === 'row').length) {
                this.setState({ rowsWarning: true })
                return
            }
            Object.keys(mappings).forEach((k) => {
                mappings[k].forEach((m) => {
                    if (m.type !== 'row') {
                        TBColumns[k] = TBColumns[k] || []
                        TBColumns[k] = [...TBColumns[k], m.value]
                    }
                    if (m.type === 'row') {
                        TBRows[k] = TBRows[k] || {}
                        TBRows[k][m.value] = TBColumns
                    }
                })
            })
        }

        targetQuestion.populateFrom = {
            pageQuestionId: sourceQuestionId,
            set1: sourceRowId,
            mapping: questionType !== 'TB' && !noMappingsQuestion ? Object.keys(mappings).reduce((obj, k) => {
                obj[k] = mappings[k].map(({ value }) => value)
                return obj
            }, {}) : {},
            gridMapping: questionType !== 'TB' ? undefined : TBRows,
        }
        try {
            await targetQuestion.updateQuestionMapping(store, targetQuestion.populateFrom)
            store.addNotification({ body: 'Question mapping updated', level: 'success' })
            trackBuilderQuestionMenuAction(store, { action: 'map responses', value: true })
        } catch (e) {
            store.handleError({ body: 'Unable to update question mapping', level: 'error' })
        }

        targetQuestion.hasPopulateFrom = true

        Object.assign(
            {},
            targetQuestion.currentOptions,
            {
                populateFrom: targetQuestion.populateFrom,
            },
        )

        this.hide()
    }

    renderTbRowMapping = () => {
        const { store } = this.props
        const { questionPipeSettings, survey } = store
        const { sourcePageQuestionId, rowsWarning, sourceQuestion } = this.state
        const { pageQuestions } = questionPipeSettings
        const sourcePageQuestion = pageQuestions.find((pq) => pq.id === sourcePageQuestionId)

        const sourceRows = R.pathOr([], ['question', 'optionSetY', 'options'], sourcePageQuestion)
            .map((o) => ({
                value: o.id,
                label: unescapeHtmlTags(o.getTitle(
                    store.surveyLocale,
                    survey.defaultLocale,
                    sourcePageQuestion.question.metadata.initialLocale,
                )),
            }))
            .map(({ value, label }, index) => (this.sourceOption(label, value, `${index}_row`)))

        return sourceQuestion ? (
            <div>
                <div className="rows__header">
                    {i18n.t('questionBuilder.rows')}
                    {rowsWarning
                        ? (
                            <span className="warning">
                                <i className="fa-light fa-circle-exclamation" />
                                {i18n.t('surveyBuilder.modal.mapping.rows_warning')}
                            </span>
                        )
                        : null}
                </div>
                <div className="rows__container">
                    {sourceRows}
                </div>
                <div className="cols__header">
                    {i18n.t('questionBuilder.cols')}
                </div>
            </div>
        ) : null
    }

    targetOptionsI = (q) => {
        const { store } = this.props
        return (
            [{
                getTitle: () => (q.questionTitle(store)),
                id: '00000000-0000-0000-0000-000000000000',
            }]
        )
    }

    toggleGuidanceAccordion = () => {
        this.setState((prevState) => ({
            ...prevState,
            isGuidanceAccordionOpen: !prevState.isGuidanceAccordionOpen,
        }))
    }

    render() {
        const { store } = this.props
        const {
            hasLiveResponses,
            sourceQuestionType,
            sourceQuestion,
            sourceOptions,
            isGuidanceAccordionOpen,
        } = this.state
        const { questionPipeSettings, showQuestionPipe } = store
        const targetQuestionType = R.pathOr(
            '',
            ['targetQuestion', 'question', 'questionTypeCode'],
            questionPipeSettings,
        )
        const optionsPath = questionOptionsPath[targetQuestionType] || ['optionSet', 'options']
        const targetOptions = targetQuestionType === 'I' && sourceQuestionType === 'NF'
            ? this.targetOptionsI(questionPipeSettings.targetQuestion)
            : R.pathOr([], ['question', ...optionsPath], questionPipeSettings.targetQuestion)
        const isTargetGrid = targetQuestionType === 'TB'
        const targetOptionsRows = isTargetGrid
            ? questionPipeSettings.targetQuestion.question.optionSetY.options
            : []

        return (
            <Modal
                show={showQuestionPipe}
                onClose={this.hide}
                cssClass="x-large"
            >
                <div id="question-mapping-modal">
                    <div className="uc-modal__header">
                        <h2>{i18n.t('surveyBuilder.modal.mapping.title')}</h2>
                        <p className="uc-modal__subtitle">{i18n.t('surveyBuilder.modal.mapping.subtitle')}</p>
                    </div>
                    <InfoAccordion
                        title={i18n.t('surveyBuilder.modal.mapping.guide.title')}
                        show={isGuidanceAccordionOpen}
                        toggle={this.toggleGuidanceAccordion}
                        cssClass="mb-35"
                        infoContentClass="p-8"
                    >
                        <p className="subtitle">{i18n.t('surveyBuilder.modal.mapping.guide.desc.1')}</p>
                        <br />
                        <p className="subtitle">{i18n.t('surveyBuilder.modal.mapping.guide.desc.2')}</p>
                    </InfoAccordion>
                    { hasLiveResponses ? (
                        <p>{i18n.t('surveyBuilder.modal.mapping.guide.desc.liveResponses')}</p>
                    ) : (
                        <div>
                            <Select
                                name={i18n.t('surveyBuilder.modal.mapping.select.label')}
                                label={i18n.t('surveyBuilder.modal.mapping.select.label')}
                                placeholder={i18n.t('download.modal.please_select')}
                                value={sourceQuestion}
                                options={questionPipeSettings.options}
                                onBlur={this.onChangeHandler}
                                isClearable={false}
                            />
                            <div>
                                { directMapQuestions.includes(targetQuestionType)
                                    ? null
                                    : (
                                        <div
                                            className="dnd__container"
                                            onDragStart={this.startDrag}
                                            onDragEnd={this.endDrag}
                                            onDragOver={this.dragOver}
                                            onDragEnter={this.dragEnter}
                                            onDragLeave={this.dragLeave}
                                            onDrop={(e) => this.drop(e)}
                                        >
                                            <Panel className="source-question">
                                                <p className="source-question__title">
                                                    {i18n.t('surveyBuilder.modal.mapping.source')}
                                                </p>
                                                <div className="source-question__container">
                                                    {isTargetGrid ? this.renderTbRowMapping() : null}
                                                    {sourceOptions.map(({ value, label }, index) => (
                                                        this.sourceOption(label, value, isTargetGrid ? `${index}_col` : index)
                                                    ))}
                                                </div>
                                            </Panel>
                                            <Panel className="destination-question">
                                                <div className="destination-question__right">
                                                    <p className="destination-question__title">
                                                        {i18n.t('surveyBuilder.modal.mapping.destination')}
                                                    </p>
                                                    {isTargetGrid ? (
                                                        <>
                                                            <div className="rows__header">
                                                                {i18n.t('questionBuilder.rows')}
                                                            </div>
                                                            <div className="rows__option-container">
                                                                {questionPipeSettings.targetQuestion && (
                                                                    targetOptionsRows.map((o, index) => (
                                                                        this.targetOption(o, index, 'dropTargetRow tb-row')
                                                                    ))
                                                                )}
                                                            </div>
                                                            <div className="cols__header">
                                                                {i18n.t('questionBuilder.cols')}
                                                            </div>
                                                        </>
                                                    ) : null}
                                                    {questionPipeSettings.targetQuestion && (
                                                        targetOptions.map((o, index) => (
                                                            this.targetOption(
                                                                o,
                                                                index,
                                                                isTargetGrid
                                                                    ? 'dropTargetRow tb-col'
                                                                    : undefined,
                                                            )
                                                        ))
                                                    )}
                                                </div>
                                            </Panel>
                                        </div>
                                    )}
                            </div>
                            <div className="uc-modal__button-panel">
                                <Button cssClass="error" onClick={this.discard}>Discard</Button>
                                <Button cssClass="primary" onClick={this.save}>Save</Button>
                            </div>
                        </div>
                    )}
                </div>
            </Modal>
        )
    }
}

export default QuestionPipe
